import React, { useState } from 'react';
import './ManualDomainAdd.css';

const ManualDomainAdd = () => {
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  const [domains, setDomains] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [progressMessages, setProgressMessages] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResults(null);
    setProgressMessages([]);

    if (!domains.trim()) {
      setError('Please enter at least one domain');
      return;
    }

    // Parse comma-separated domains
    const domainList = domains
      .split(',')
      .map(d => d.trim())
      .filter(d => d.length > 0);

    if (domainList.length === 0) {
      setError('No valid domains found');
      return;
    }

    setLoading(true);
    addProgressMessage('Starting domain processing...', 'info');
    addProgressMessage(`Processing ${domainList.length} domain(s)`, 'info');

    try {
      const response = await fetch(`${basePath}/api/manual-adding/process`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domains: domainList
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
        addProgressMessage('Processing completed successfully!', 'success');
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || `Server error: ${response.status}`;
        setError(errorMessage);
        addProgressMessage(`Error: ${errorMessage}`, 'error');
        console.error('API response not ok:', response.status, errorData);
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to process domains';
      setError(errorMessage);
      addProgressMessage(`Error: ${errorMessage}`, 'error');
      console.error('Error processing domains:', err);
    } finally {
      setLoading(false);
    }
  };

  const addProgressMessage = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setProgressMessages(prev => [...prev, { message, type, timestamp }]);
  };

  const handleReset = () => {
    setDomains('');
    setResults(null);
    setError('');
    setProgressMessages([]);
  };

  return (
    <div className="manual-domain-add-container">
      <div className="header-section">
        <h1>Manual Domain Processing</h1>
        <p className="subtitle">Process domains through SEMrush pipeline (Domain Rank → Organic Keywords → Paid Keywords → Geo Data → Merge)</p>
      </div>

      <form onSubmit={handleSubmit} className="manual-domain-form">
        <div className="form-group">
          <label htmlFor="domains">
            Domains <span className="required">*</span>
          </label>
          <textarea
            id="domains"
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder="Enter domains separated by commas&#10;Example: domain1.com, domain2.com, domain3.com"
            rows={8}
            disabled={loading}
            required
          />
          <small className="help-text">
            Separate multiple domains with commas. Each domain will be processed through the complete SEMrush pipeline (Domain Rank → Organic Keywords → Paid Keywords → Geo Data → Merge). No brand association required.
          </small>
        </div>

        <div className="button-group">
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? (
              <>
                <span className="spinner"></span>
                Processing...
              </>
            ) : (
              'Process Domains'
            )}
          </button>
          {!loading && (results || error) && (
            <button type="button" onClick={handleReset} className="reset-btn">
              Reset Form
            </button>
          )}
        </div>
      </form>

      {progressMessages.length > 0 && (
        <div className="progress-section">
          <h3>Progress Log</h3>
          <div className="progress-messages">
            {progressMessages.map((msg, index) => (
              <div key={index} className={`progress-message ${msg.type}`}>
                <span className="timestamp">[{msg.timestamp}]</span>
                <span className="message">{msg.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <div className="message-icon">⚠️</div>
          <div className="message-content">
            <strong>Error:</strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      {results && (
        <div className="results-container">
          <h2>Processing Results</h2>

          <div className="results-summary">
            <div className="summary-card total">
              <div className="card-icon">📊</div>
              <div className="card-content">
                <div className="card-value">{results.summary?.totalDomains || 0}</div>
                <div className="card-label">Total Processed</div>
              </div>
            </div>
            <div className="summary-card success">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <div className="card-value">{results.summary?.successful || 0}</div>
                <div className="card-label">Successful</div>
              </div>
            </div>
            <div className="summary-card failed">
              <div className="card-icon">❌</div>
              <div className="card-content">
                <div className="card-value">{results.summary?.failed || 0}</div>
                <div className="card-label">Failed</div>
              </div>
            </div>
          </div>

          {/* Additional metrics */}
          {results.summary && (
            <div className="metrics-summary">
              <div className="metric-item">
                <span className="metric-icon">🔑</span>
                <span className="metric-label">Organic Keywords:</span>
                <span className="metric-value">{results.summary.organicKeywords || 0}</span>
              </div>
              <div className="metric-item">
                <span className="metric-icon">💰</span>
                <span className="metric-label">Paid Keywords:</span>
                <span className="metric-value">{results.summary.paidKeywords || 0}</span>
              </div>
              <div className="metric-item">
                <span className="metric-icon">🌍</span>
                <span className="metric-label">Geo Records:</span>
                <span className="metric-value">{results.summary.geoRecords || 0}</span>
              </div>
            </div>
          )}

          {results.steps?.step1_domainRank?.results && results.steps.step1_domainRank.results.length > 0 && (
            <div className="domain-results">
              <h3>Domain Processing Details</h3>
              {results.steps.step1_domainRank.results.map((result, index) => (
                <div key={index} className={`domain-result ${result.success ? 'success' : 'failure'}`}>
                  <div className="result-header">
                    <h4>{result.domain}</h4>
                    <span className={`status-badge ${result.success ? 'success' : 'failure'}`}>
                      {result.success ? '✓ Success' : '✗ Failed'}
                    </span>
                  </div>

                  <div className="result-body">
                    {result.inserted !== undefined && (
                      <div className="result-stat">
                        <strong>Records Inserted:</strong> {result.inserted}
                      </div>
                    )}

                    {result.usedFallback && (
                      <div className="result-note fallback">
                        <strong>Note:</strong> Processed via SemrushController fallback (minimal data)
                      </div>
                    )}

                    {result.message && (
                      <div className="result-message">
                        <strong>Message:</strong> {result.message}
                      </div>
                    )}

                    {result.error && (
                      <div className="error-text">
                        <strong>Error:</strong> {result.error}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManualDomainAdd;
